import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EloBadge from "../common/EloBadge.js";
import utils from "../../universal/utils";
import {
  pickAvatarUpdatedAtMs,
  resolveAvatarSrc,
} from "../../utils/avatarUrl";

import CircularProgress from "@mui/material/CircularProgress";

const basePath = utils.getEdgeBasePath();

const useStyles = makeStyles((theme) => ({
    friendRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.38rem',
        padding: '0.2rem 0.3rem',
        marginBottom: '0.1rem',
        background: 'transparent',
        borderRadius: '0',
        border: 'none',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.03)',
        },
        '&.offline': {
            background: 'transparent',
            border: 'none',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            opacity: 0.7,
            '&:hover': {
                background: 'rgba(255, 255, 255, 0.02)',
                opacity: 0.8,
            }
        },
        [theme.breakpoints.up("xl")]: {
            padding: '0.24rem 0.36rem',
            marginBottom: '0.12rem',
        },
    },
    avatarContainer: {
        position: 'relative',
        flexShrink: 0,
    },
    avatar: {
        width: '1.8rem',
        height: '1.8rem',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        transition: 'all 0.3s ease',
        '.offline &': {
            border: '1px solid rgba(128, 128, 128, 0.2)',
            filter: 'grayscale(0.3)',
        },
        [theme.breakpoints.up("xl")]: {
            width: '1.95rem',
            height: '1.95rem',
        },
    },
    statusIndicator: {
        position: 'absolute',
        bottom: '1px',
        right: '1px',
        width: '0.55rem',
        height: '0.55rem',
        borderRadius: '50%',
        transition: 'all 0.3s ease',
        [theme.breakpoints.up("xl")]: {
            width: '0.65rem',
            height: '0.65rem',
        },
    },
    statusOnline: {
        background: '#38b000',
        boxShadow: 'none',
    },
    statusOffline: {
        background: '#666666',
        boxShadow: 'none',
    },
    friendInfo: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.1rem',
        minWidth: 0,
    },
    friendName: {
        color: '#ffffff',
        fontSize: '0.72rem',
        fontWeight: 600,
        letterSpacing: '0.2px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        transition: 'color 0.3s ease',
        '.offline &': {
            color: '#9ca3af',
        },
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.76rem',
        },
    },
    eloContainer: {
        display: 'flex',
        alignItems: 'center',
    },
    actionsContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '0.18rem',
        flexShrink: 0,
    },
    inviteButton: {
        background: 'rgba(232, 163, 61, 0.15)',
        color: '#ffffff',
        fontSize: '0.66rem',
        fontWeight: 600,
        padding: '0.1rem 0.45rem',
        borderRadius: '5px',
        border: '1px solid rgba(232, 163, 61, 0.65)',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        textTransform: 'none',
        letterSpacing: '0.2px',
        boxShadow: 'none',
        minHeight: '28px',
        '&:hover': {
            background: 'rgba(232, 163, 61, 0.28)',
            boxShadow: 'none',
        },
        '&:active': {
            transform: 'translateY(0)',
        },
        [theme.breakpoints.up("xl")]: {
            padding: '0.12rem 0.5rem',
            fontSize: '0.68rem',
        },
    },
    inviteButtonIcon: {
        [theme.breakpoints.up("xl")]: {
            fontSize: '1.5rem !important',
        },
    },
    inviteButtonDisabled: {
        background: 'rgba(255, 255, 255, 0.06)',
        boxShadow: 'none',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.06)',
            transform: 'translateY(0)',                         
            boxShadow: 'none',
        },
    },
    buttonInvited: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.4rem',
        background: 'rgba(255, 255, 255, 0.06)',
        color: '#e2e8f0',
        textTransform: 'none',
        fontSize: '0.64rem',
        fontWeight: 600,
        padding: '0.1rem 0.45rem',
        borderRadius: '5px',
        border: '1px solid rgba(255, 255, 255, 0.16)',
        userSelect: 'none',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        [theme.breakpoints.up("xl")]: {
            padding: '0.12rem 0.5rem',
            fontSize: '0.68rem',
        },
    },
    buttonInvitedProgress: {
        [theme.breakpoints.up("xl")]: {
            width: '1.5rem !important',
            height: '1.5rem !important',
        },
    },
    buttonInvitedText: {
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.82rem',
        },
    },
    buttonInvitedError: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.4rem',
        background: 'rgba(220, 20, 60, 0.12)',
        color: '#e2e8f0',
        textTransform: 'none',
        fontSize: '0.64rem',
        fontWeight: 600,
        padding: '0.1rem 0.45rem',
        borderRadius: '5px',
        border: '1px solid rgba(220, 20, 60, 0.42)',
        userSelect: 'none',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        [theme.breakpoints.up("xl")]: {
            padding: '0.12rem 0.5rem',
            fontSize: '0.68rem',
        },
    },
    buttonInvitedErrorText: {
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.82rem',
        },
    },
    buttonInTeam: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.3rem',
        background: 'rgba(135, 206, 235, 0.09)',
        color: '#e2e8f0',
        textTransform: 'none',
        fontSize: '0.62rem',
        fontWeight: 600,
        padding: '0.1rem 0.4rem',
        borderRadius: '5px',
        border: '1px solid rgba(135, 206, 235, 0.42)',
        userSelect: 'none',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        [theme.breakpoints.up("xl")]: {
            padding: '0.12rem 0.46rem',
            fontSize: '0.66rem',
        },
    },
    buttonInTeamText: {
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.8rem',
        },
    },
    buttonTeamFull: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.3rem',
        background: 'rgba(156, 163, 175, 0.08)',
        color: '#9ca3af',
        textTransform: 'none',
        fontSize: '0.62rem',
        fontWeight: 600,
        padding: '0.1rem 0.4rem',
        borderRadius: '5px',
        border: '1px solid rgba(156, 163, 175, 0.25)',
        userSelect: 'none',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
        cursor: 'default',
        opacity: 0.7,
        [theme.breakpoints.up("xl")]: {
            padding: '0.12rem 0.46rem',
            fontSize: '0.66rem',
        },
    },
    buttonTeamFullText: {
        [theme.breakpoints.up("xl")]: {
            fontSize: '0.8rem',
        },
    },
    menuButton: {
        color: '#cfd4d9',
        padding: '0.1rem',
        minWidth: '28px',
        minHeight: '28px',
        transition: 'all 0.3s ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        '.offline &': {
            color: '#9ca3af',
            '&:hover': {
                backgroundColor: 'rgba(156, 163, 175, 0.1)',
            }
        },
        [theme.breakpoints.up("xl")]: {
            padding: '0.14rem',
        },
    },
    menuButtonIcon: {
        [theme.breakpoints.up("xl")]: {
            fontSize: '1.65rem',
        },
    },
    menuButtonDisabled: {
        color: '#999999',
    },
    menu: {
        zIndex: 9999,
        padding: 0,
        '& .MuiPaper-root': {
            background: '#2A2D30',
            minWidth: '8rem',
            border: '1px solid rgba(232, 163, 61, 0.2)',
            borderRadius: '8px',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            [theme.breakpoints.up("xl")]: {
                minWidth: '10rem',
            },
        }
    },
    menuItem: {
        width: '100%',
        color: '#ffffff',
        fontSize: '0.75rem',
        padding: '0.5rem 1.8rem',
        textTransform: 'uppercase',
        borderRadius: '8px',
        transition: 'all 0.3s ease',
        [theme.breakpoints.up("xl")]: {
            padding: '0.65rem 2.25rem',
            fontSize: '0.9rem',
        },
    },
    menuItemProfile: {
        backgroundColor: '#1E90FF',
        '&:hover': {
            backgroundColor: '#0073da',
        }
    },
    menuItemRemove: {
        marginTop: '0.4rem',
        backgroundColor: '#A52A2A',
        '&:hover': {
            backgroundColor: '#8B0000',
        }
    },
}));

export default function FriendRow({ 
    friend, 
    handleDeleteFriend, 
    sendInvite, 
    inviteState = {},
    handleClearInviteError,
    lobbyState,
    showUnavailableActionMessage,
    currentLobbyCode,
    isInMyLobby = false,
    isLobbyFull = false,
    isLeader = true,
}) {
    const classes = useStyles();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const history = useHistory();

    const friendData = friend.friendData;

    if (!friendData) {
        return null;
    }

    const avatarSrc = resolveAvatarSrc({
      basePath,
      picture: friendData?.picture,
      userId: friendData?.userId || friendData?._id,
      avatarUpdatedAtMs: pickAvatarUpdatedAtMs(friendData),
      hasCustomAvatar: !!friendData?.avatar,
    });
    const isOnline = friendData ? friendData.isOnline : false;

    const playerStatus = friendData.playerStatus || (isOnline ? 'inLobby' : null);
    
    // Derived invite error flags and normalized message
    const inviteError = inviteState.inviteError;
    const inviteErrorLower = inviteError ? String(inviteError).toLowerCase() : '';
    const isInviteExpired = inviteErrorLower.includes('expired');
    const isInviteDeclined = inviteErrorLower.includes('declined');
    
    const handleMenuOpen = (event) => {
        setMenuAnchor(event.currentTarget);
    };
    
    const handleMenuClose = () => {
        setMenuAnchor(null);
    };
    
    const handleInvite = () => {
        sendInvite(friend);
    };

    const handleDelete = () => {
        handleDeleteFriend(friend.friendSteamId);
    };

    const handleProfile = () => {
        history.push(`/profile/${friendData._id}`);
    };

    useEffect(() => {
        if (!inviteState.inviteError) return;

        // Auto-clear transient invite errors like expired/declined after 3s (case-insensitive)
        const lower = String(inviteState.inviteError).toLowerCase();
        if (lower.includes('expired') || lower.includes('declined')) {
            const timeout = setTimeout(() => {
                handleClearInviteError(friendData.userId);
            }, 3000);
            return () => clearTimeout(timeout);
        }
    }, [inviteState.inviteError, friendData.userId, handleClearInviteError]);

    return (
        <div className={`${classes.friendRow} ${!isOnline ? 'offline' : ''}`}>
            <div className={classes.avatarContainer}>
                <Avatar 
                    className={classes.avatar}
                    src={avatarSrc}
                    alt={friendData.nickname}
                />
                <div 
                    className={`${classes.statusIndicator} ${
                        isOnline ? classes.statusOnline : classes.statusOffline
                    }`}
                />
            </div>
            
            <div className={classes.friendInfo}>
                <Typography className={classes.friendName}>
                    {friendData.nickname}
                </Typography>
                <div className={classes.eloContainer}>
                    <EloBadge value={friendData.elo} size="small" />
                </div>
            </div>
            
            <div className={classes.actionsContainer}>
                {isOnline && (
                    <>
                        {inviteState.inviteError ? (
                            <div className={classes.buttonInvitedError}>
                                <Typography className={classes.buttonInvitedErrorText}>
                                    {isInviteExpired
                                        ? 'Invite Expired'
                                        : isInviteDeclined
                                            ? 'Invite Declined'
                                            : inviteState.inviteError}
                                </Typography>
                            </div>
                        ) : inviteState.invited && !inviteState.inviteAccepted ? (
                            <div className={classes.buttonInvited}>
                                <CircularProgress size={20} className={classes.buttonInvitedProgress}/>
                                <Typography className={classes.buttonInvitedText}>Invited...</Typography>
                            </div>
                        ) : isInMyLobby ? (
                            <div className={classes.buttonInTeam}>
                                <Typography className={classes.buttonInTeamText}>In Your Lobby</Typography>
                            </div>
                        ) : inviteState.inviteAccepted ? (
                            <div className={classes.buttonInTeam}>
                                <Typography className={classes.buttonInTeamText}>In Your Team</Typography>
                            </div>
                        ) : playerStatus === 'inPrivateLobby' && !inviteState.invited && !inviteState.inviteAccepted ? (
                            <div className={classes.buttonInTeam}>
                                <Typography className={classes.buttonInTeamText}>In a Team</Typography>
                            </div>
                        ) : playerStatus === 'inLobby' && !inviteState.invited && !inviteState.inviteAccepted ? (
                            !isLeader && lobbyState !== 'searching' && currentLobbyCode ? (
                                <div className={classes.buttonInTeam}>
                                    <Typography className={classes.buttonInTeamText}>Leader Only</Typography>
                                </div>
                            ) : isLobbyFull ? (
                                <div className={classes.buttonTeamFull}>
                                    <Typography className={classes.buttonTeamFullText}>Team Full</Typography>
                                </div>
                            ) : (
                                <Button
                                    className={`${classes.inviteButton} ${lobbyState === "searching" ? classes.inviteButtonDisabled : ""}`}
                                    onClick={() => {
                                        if (lobbyState === "searching") {
                                            showUnavailableActionMessage();
                                        } else {
                                            handleInvite();
                                        }
                                    }}
                                    startIcon={<PersonAddIcon className={classes.inviteButtonIcon}/>}
                                >
                                    Invite
                                </Button>
                            )
                        ) : null}
                    </>
                )}
                
                <IconButton
                    className={`${classes.menuButton} ${lobbyState === "searching" ? classes.menuButtonDisabled : ""}`}
                    onClick={(event) => {
                        if (lobbyState === "searching") {
                            showUnavailableActionMessage();
                        } else {
                            handleMenuOpen(event);
                        }
                    }}
                    size="small"
                >
                    <MoreVertIcon className={classes.menuButtonIcon}/>
                </IconButton>
                
                <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={handleMenuClose}
                    className={classes.menu}
                    disableScrollLock={true}
                    getContentAnchorEl={null}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <MenuItem 
                        className={`${classes.menuItem} ${classes.menuItemProfile}`}
                        onClick={handleProfile}
                    >
                        Profile
                    </MenuItem>
                    <MenuItem 
                        className={`${classes.menuItem} ${classes.menuItemRemove}`}
                        onClick={handleDelete}
                    >
                        Remove
                    </MenuItem>
                </Menu>
            </div>
        </div>
    );
}